import configData from '../../config.json';
import { IS_PROD } from '../../utils/helpers/constants.helper';

let hasAnalyticsConsent = false;

function isUnresolvedToken(value) {
  return typeof value === 'string' && /#\{.+\}#/.test(value);
}

const instrumentationKey = configData.instrumentationKey;

const hasValidInstrumentationKey =
  typeof instrumentationKey === 'string' &&
  instrumentationKey.trim() !== '' &&
  !isUnresolvedToken(instrumentationKey);

export const isAppInsightsEnabled = IS_PROD && hasValidInstrumentationKey;

let appInsights = null;
let reactPlugin = null;
let initPromise = null;

const telemetryQueue = [];
const MAX_QUEUE_SIZE = 30;

function enqueueTelemetry(item) {
  if (telemetryQueue.length >= MAX_QUEUE_SIZE) {
    telemetryQueue.shift();
  }

  telemetryQueue.push({
    ...item,
    queuedAt: new Date().toISOString(),
  });
}

export function setAppInsightsConsent(consentGranted) {
  hasAnalyticsConsent = Boolean(consentGranted);

  if (appInsights) {
    appInsights.config.disableTelemetry = !hasAnalyticsConsent;
  }

  if (!hasAnalyticsConsent) {
    telemetryQueue.length = 0;
  }
}

function flushTelemetryQueue() {
  if (!appInsights || telemetryQueue.length === 0) {
    return;
  }

  while (telemetryQueue.length > 0) {
    const item = telemetryQueue.shift();

    if (item.type === 'event') {
      appInsights.trackEvent(
        { name: item.name },
        {
          ...item.properties,
          queuedAt: item.queuedAt,
        },
      );
    }

    if (item.type === 'pageView') {
      appInsights.trackPageView({
        ...item.pageView,
        properties: {
          ...(item.pageView?.properties || {}),
          queuedAt: item.queuedAt,
        },
      });
    }

    if (item.type === 'exception') {
      appInsights.trackException({
        exception: item.error,
        properties: {
          ...item.properties,
          queuedAt: item.queuedAt,
        },
      });
    }
  }
}

export function getAppInsights() {
  return appInsights;
}

export function isAppInsightsReady() {
  return Boolean(appInsights);
}

export async function initAppInsights() {
  if (!isAppInsightsEnabled || !hasAnalyticsConsent) {
    return null;
  }

  if (appInsights) {
    return appInsights;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = Promise.all([
    import(
      /* webpackChunkName: "app-insights" */
      '@microsoft/applicationinsights-web'
    ),
    import(
      /* webpackChunkName: "app-insights-react" */
      '@microsoft/applicationinsights-react-js'
    ),
  ])
    .then(([appInsightsModule, reactPluginModule]) => {
      const { ApplicationInsights } = appInsightsModule;
      const { ReactPlugin } = reactPluginModule;

      reactPlugin = new ReactPlugin();

      appInsights = new ApplicationInsights({
        config: {
          instrumentationKey,
          extensions: [reactPlugin],
          disablePageUnloadEvents: ['unload'],
        },
      });

      appInsights.loadAppInsights();
      flushTelemetryQueue();

      return appInsights;
    })
    .catch((error) => {
      initPromise = null;

      if (!IS_PROD) {
        // eslint-disable-next-line no-console
        console.warn('Failed to initialize App Insights', error);
      }

      return null;
    });

  return initPromise;
}

function runWhenIdle(callback) {
  if (typeof window === 'undefined') {
    return;
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 3000 });
    return;
  }

  window.setTimeout(callback, 3000);
}

export function initAppInsightsWhenIdle() {
  if (!isAppInsightsEnabled || !hasAnalyticsConsent) {
    return;
  }

  runWhenIdle(() => {
    initAppInsights();
  });
}

export function trackAppEvent(name, properties = {}) {
  if (!isAppInsightsEnabled || !hasAnalyticsConsent) {
    return;
  }

  if (!appInsights) {
    enqueueTelemetry({
      type: 'event',
      name,
      properties,
    });
    return;
  }

  appInsights.trackEvent({ name }, properties);
}

export function trackAppPageView(pageView = {}) {
  if (!isAppInsightsEnabled || !hasAnalyticsConsent) {
    return;
  }

  if (!appInsights) {
    enqueueTelemetry({
      type: 'pageView',
      pageView,
    });
    return;
  }

  appInsights.trackPageView(pageView);
}

export function trackAppException(error, properties = {}) {
  if (!isAppInsightsEnabled || !hasAnalyticsConsent) {
    return;
  }

  if (!appInsights) {
    enqueueTelemetry({
      type: 'exception',
      error,
      properties,
    });
    return;
  }

  appInsights.trackException({
    exception: error,
    properties,
  });
}
